import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Eye, ChevronDown } from 'lucide-react';
import { Modal, Row, Col } from 'react-bootstrap';
import {
    fetchAdminFeedback,
    type AdminFeedbackItem,
    type ChatFeedbackReason,
    type ChatFeedbackType,
} from '../../services/api';
import Preloader from '../../components/Preloder';
import { useTimezoneFormatting } from '../../hooks/useTimezoneFormatting';
import totalUsers from '../../assets/icons/totalUsers.png';
import chatBotInteraction from '../../assets/icons/chatBotInteraction.png';
import queriedSOP from '../../assets/icons/queriedSOP.png';
import { formatAIText } from 'src/utils/formatAIText';

const FEEDBACK_TYPE_FILTER_ALL = '' as const;
const REASON_FILTER_ALL = '' as const;

const REASON_OPTIONS: { value: ChatFeedbackReason; label: string }[] = [
    { value: 'WRONG_ANSWER', label: 'Wrong answer' },
    { value: 'IRRELEVANT_SOP', label: 'Irrelevant SOP' },
    { value: 'MISSING_INFORMATION', label: 'Missing information' },
    { value: 'HALLUCINATION', label: 'Hallucination (made-up content)' },
    { value: 'TOO_GENERIC', label: 'Too generic' },
    { value: 'OTHER', label: 'Other' },
];

const REASON_LABELS: Record<string, string> = Object.fromEntries(
    REASON_OPTIONS.map((o) => [o.value, o.label])
) as Record<string, string>;

type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };

function getPageNumbers(current: number, total: number): PageButton[] {
    const buttons: PageButton[] = [];
    const pushPage = (p: number, active = false) =>
        buttons.push({ key: `p-${p}`, page: p, label: p, active });
    const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true });
    if (total <= 5) {
        for (let i = 1; i <= total; i++) pushPage(i, i === current);
        return buttons;
    }
    pushPage(1, current === 1);
    if (current > 3) pushEllipsis('l');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i, i === current);
    if (current < total - 2) pushEllipsis('r');
    pushPage(total, current === total);
    return buttons;
}

function pickString(item: AdminFeedbackItem, ...keys: string[]): string {
    for (const k of keys) {
        const v = item[k];
        if (v === null || v === undefined) continue;
        if (typeof v === 'string' && v.trim()) return v;
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    }
    return '';
}

/** Naive UTC ISO from the API has no suffix; app timezone helpers expect UTC-marked input. */
function normalizeUtcInputForApp(iso: string): string {
    const s = (iso || '').trim();
    if (!s) return s;
    if (s.endsWith('Z')) return s;
    if (/[+-]\d{2}:\d{2}$/.test(s)) return s;
    return `${s}Z`;
}

function pickFeedbackActivityIso(item: AdminFeedbackItem): string {
    return pickString(
        item,
        'updated_at',
        'updatedAt',
        'created_at',
        'createdAt',
        'timestamp',
        'date',
        'submitted_at',
        'submittedAt',
    );
}

function getFeedbackType(item: AdminFeedbackItem): ChatFeedbackType | '' {
    const raw = pickString(item, 'feedback_type', 'feedbackType', 'type', 'rating').toUpperCase();
    if (raw === 'UP' || raw === 'THUMBS_UP' || raw === 'POSITIVE') return 'UP';
    if (raw === 'DOWN' || raw === 'THUMBS_DOWN' || raw === 'NEGATIVE') return 'DOWN';
    return '';
}

function getItemTimestamp(item: AdminFeedbackItem): number {
    const ds = pickFeedbackActivityIso(item);
    if (!ds) return 0;
    const d = new Date(normalizeUtcInputForApp(ds));
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function formatSopsReadable(raw: unknown): string[] {
    if (raw == null) return [];
    if (Array.isArray(raw)) {
        return raw
            .map((entry) => {
                if (typeof entry === 'string') return entry.trim();
                if (entry && typeof entry === 'object') {
                    const o = entry as Record<string, unknown>;
                    const line = [
                        pickString(
                            o as AdminFeedbackItem,
                            'sopTitle',
                            'sop_title',
                            'title',
                            'name',
                            'sop_name',
                            'sopName',
                            'document_name',
                            'documentName',
                        ),
                        pickString(o as AdminFeedbackItem, 'section', 'section_title', 'sectionTitle'),
                    ]
                        .filter(Boolean)
                        .join(' — ');
                    return line || '';
                }
                return String(entry);
            })
            .filter(Boolean);
    }
    return [];
}

function toTitle(v: string) {
    return (v || '').replace(/_/g, ' ').replace(/^\w|\s\w/g, (m) => m.toUpperCase());
}

function FeedbackStatCard({
    title,
    value,
    iconSrc,
    valueVariant = 'default',
    subtitle,
}: {
    title: string;
    value: string;
    iconSrc: string;
    valueVariant?: 'default' | 'capsule';
    subtitle?: string;
}) {
    return (
        <div className="dashboard-stat-card">
            {/* <div className="position-absolute top-0 start-0 w-100 h-100 rounded-3xl border-top border-start border-danger" style={{borderColor: '#fecaca', pointerEvents: 'none'}} /> */}
            <div className="d-flex flex-column h-100 justify-content-between">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-16-reg grey-600 mt-1">{title}</h6>
                    <div className="stat-card-icon">
                        <img src={iconSrc} alt="" style={{ width: '16px', height: '16px' }} />
                    </div>
                </div>
                {valueVariant === "capsule" ? (
                    <div className="mt-1 w-100 text-start">
                        <p
                            className="text-12-bold grey-800"
                            style={{
                                backgroundColor: '#F7F7F7',
                                padding: '12px 16px',
                                borderRadius: '40px',
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                width: '100%'
                            }}
                            title={value}
                        >
                            {value}
                        </p>
                    </div>
                ) : (
                    <h1 className="grey-900">{value}</h1>
                )}
            </div>
        </div>
    );
}

function FeedbackDetailModal({
    item,
    onClose,
    formatToLocal,
}: {
    item: AdminFeedbackItem;
    onClose: () => void;
    formatToLocal: (timestamp: string, options?: Intl.DateTimeFormatOptions) => string;
}) {
    const ft = getFeedbackType(item);
    const reasonRaw = pickString(item, 'reason');
    const reasonLabel = reasonRaw ? REASON_LABELS[reasonRaw] || toTitle(reasonRaw) : 'N/A';
    // const source = pickString(item, 'source', 'app_source', 'appSource', 'surface', 'role');
    const ts = pickFeedbackActivityIso(item);
    const user = pickString(item, 'userEmail', 'user_email', 'user', 'user_name', 'userName', 'submitted_by', 'submittedBy');
    const query = pickString(item, 'user_query', 'userQuery', 'query', 'question', 'user_message', 'userMessage');
    const aiResponse = pickString(item, 'ai_response', 'aiResponse', 'assistant_response', 'assistantResponse', 'response', 'answer');
    const comments = pickString(item, 'comments', 'comment', 'comments_text', 'commentsText');
    const sops = formatSopsReadable(
        item.retrievedDocs ??
        item.retrieved_docs ??
        item.retrieved_sops ??
        item.retrievedSops ??
        item.sops,
    );
    const model = pickString(item, 'model', 'llm_model', 'llmModel');
    const promptVersion = pickString(item, 'prompt_version', 'promptVersion');
    const rerankerVersion = pickString(item, 'reranker_version', 'rerankerVersion');
    const pipelineContext = item.pipeline_context ?? item.pipelineContext;
    const compliance = item.compliance;
    const retrievalMeta = item.retrieval_metadata ?? item.retrievalMetadata;

    const tsNorm = ts ? normalizeUtcInputForApp(ts) : '';
    const tsDisplay =
        tsNorm && !Number.isNaN(new Date(tsNorm).getTime())
            ? formatToLocal(tsNorm, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
            : ts || 'N/A';

    const pretty = (v: unknown) => {
        if (v === null || v === undefined) return '';
        try {
            return JSON.stringify(v, null, 2);
        } catch {
            return String(v);
        }
    };

    return (
        <Modal show onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Feedback detail</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose} role="button" aria-label="Close" />
            </Modal.Header>
            <Modal.Body className="px-4 py-4">
                <div className="overflow-y-auto pe-1" style={{ maxHeight: '72vh' }}>
                    <section className="mb-4">
                        <h5 className="text-14-bold grey-800 mb-3">Feedback summary</h5>
                        <div className="row g-3">
                            <div className="col-md-6 mb-3">
                                <p className="text-12-bold grey-500 mb-1 text-uppercase">Feedback type</p>
                                {ft === 'UP' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-12-bold bg-green-100 text-green-800">
                                        UP
                                    </span>
                                )}
                                {ft === 'DOWN' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-12-bold bg-red-100 text-red-800">
                                        DOWN
                                    </span>
                                )}
                                {!ft && <span className="text-14-reg grey-700">N/A</span>}
                            </div>
                            <div className="col-md-6 mb-3">
                                <p className="text-12-bold grey-500 mb-1 text-uppercase">Reason</p>
                                <span className="px-2 py-1 rounded-md text-12-bold bg-gray-100 text-gray-800">{reasonLabel}</span>
                            </div>
                            {/* <div className="col-md-6 mb-3"> */}
                            {/* <p className="text-12-bold grey-500 mb-1 text-uppercase">Source</p> */}
                            {/* <p className="text-14-reg grey-800 mb-0">{source || '—'}</p> */}
                            {/* </div> */}
                            <div className="col-md-6 mb-3">
                                <p className="text-12-bold grey-500 mb-1 text-uppercase">Last updated</p>
                                <p className="text-14-reg grey-800 mb-0">{tsDisplay}</p>
                            </div>
                            <div className="col-md-6 mb-3" >
                                <p className="text-12-bold grey-500 mb-1 text-uppercase">User email</p>
                                <p className="text-14-reg grey-800 mb-0">{user || 'N/A'}</p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-4">
                        <h5 className="text-14-bold grey-800 mb-2">User query</h5>
                        <div className="text-14-reg grey-800 bg-grey-50 rounded-lg px-3 py-3 whitespace-pre-wrap break-words">
                            {query || 'N/A'}
                        </div>
                    </section>

                    <section className="mb-4">
                        <h5 className="text-14-bold grey-800 mb-2">AI response</h5>
                        <div className="text-14-reg grey-700 admin-ai-chat-log bg-grey-50 rounded-lg px-3 py-3 break-words">
                            {aiResponse ? formatAIText(aiResponse) : 'N/A'}
                        </div>
                    </section>

                    <section className="mb-4">
                        <h5 className="text-14-bold grey-800 mb-2">User comments</h5>
                        <div className="text-14-reg grey-700 admin-ai-chat-log bg-grey-50 rounded-lg px-3 py-3 whitespace-pre-wrap break-words">
                            {comments || 'N/A'}
                        </div>
                    </section>

                    <section className="mb-4">
                        <h5 className="text-14-bold grey-800 mb-2">Retrieved SOPs</h5>
                        {sops.length === 0 ? (
                            <p className="text-14-reg grey-700 admin-ai-chat-log bg-grey-50 rounded-lg px-3 py-3 whitespace-pre-wrap break-words">N/A</p>
                        ) : (
                            <ul className="list-unstyled mb-0 space-y-2">
                                {sops.map((line, i) => (
                                    <li key={i} className="text-14-reg grey-700 admin-ai-chat-log bg-grey-50 rounded-lg px-3 py-3 whitespace-pre-wrap break-words">
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* <section className="mb-3">
                        <h5 className="text-14-bold grey-800 mb-2">AI metadata</h5>
                        <div className="row g-2">
                            <div className="col-md-4">
                                <p className="text-12-bold grey-500 mb-0">Model</p>
                                <p className="text-14-reg grey-800">{model || '—'}</p>
                            </div>
                            <div className="col-md-4">
                                <p className="text-12-bold grey-500 mb-0">Prompt version</p>
                                <p className="text-14-reg grey-800">{promptVersion || '—'}</p>
                            </div>
                            <div className="col-md-4">
                                <p className="text-12-bold grey-500 mb-0">Reranker version</p>
                                <p className="text-14-reg grey-800">{rerankerVersion || '—'}</p>
                            </div>
                        </div>
                    </section>

                    <details className="border border-gray-200 rounded-xl p-3 bg-white">
                        <summary className="text-14-bold grey-800 cursor-pointer">Technical details</summary>
                        <div className="mt-3 space-y-3">
                            {pipelineContext != null && (
                                <div>
                                    <p className="text-12-bold grey-500 mb-1">pipelineContext</p>
                                    <pre className="text-12-reg grey-800 bg-gray-50 p-2 rounded-lg overflow-auto mb-0" style={{ maxHeight: '12rem' }}>
                                        {pretty(pipelineContext)}
                                    </pre>
                                </div>
                            )}
                            {compliance != null && (
                                <div>
                                    <p className="text-12-bold grey-500 mb-1">compliance</p>
                                    <pre className="text-12-reg grey-800 bg-gray-50 p-2 rounded-lg overflow-auto mb-0" style={{ maxHeight: '12rem' }}>
                                        {pretty(compliance)}
                                    </pre>
                                </div>
                            )}
                            {retrievalMeta != null && (
                                <div>
                                    <p className="text-12-bold grey-500 mb-1">Raw retrieval metadata</p>
                                    <pre className="text-12-reg grey-800 bg-gray-50 p-2 rounded-lg overflow-auto mb-0" style={{ maxHeight: '12rem' }}>
                                        {pretty(retrievalMeta)}
                                    </pre>
                                </div>
                            )}
                            {pipelineContext == null && compliance == null && retrievalMeta == null && (
                                <p className="text-14-reg grey-600 mb-0">No technical payloads on this record.</p>
                            )}
                        </div>
                    </details> */}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" onClick={onClose} className="renegade-btn-secondary mini-btn">
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export const AdminFeedback: React.FC = () => {
    const { formatToLocal } = useTimezoneFormatting();
    const [search, setSearch] = useState('');
    const [feedbackType, setFeedbackType] = useState<typeof FEEDBACK_TYPE_FILTER_ALL | ChatFeedbackType>(FEEDBACK_TYPE_FILTER_ALL);
    const [reason, setReason] = useState<typeof REASON_FILTER_ALL | ChatFeedbackReason>(REASON_FILTER_ALL);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);
    const [items, setItems] = useState<AdminFeedbackItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [detailItem, setDetailItem] = useState<AdminFeedbackItem | null>(null);
    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
    const typeDropdownRef = useRef<HTMLDivElement>(null);
    const reasonDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
                setTypeDropdownOpen(false);
            }
            if (reasonDropdownRef.current && !reasonDropdownRef.current.contains(event.target as Node)) {
                setReasonDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchAdminFeedback({
                    page,
                    pageSize,
                    search,
                    feedbackType: feedbackType || undefined,
                    reason: reason || undefined,
                    dateFrom: dateFrom || undefined,
                    dateTo: dateTo || undefined,
                });
                if (cancelled) return;
                const raw = res.items || [];
                const sorted = [...raw].sort((a, b) => getItemTimestamp(b) - getItemTimestamp(a));
                setItems(sorted);
                setTotal(typeof res.total === 'number' ? res.total : 0);
            } catch (e: unknown) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load feedback');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize, search, feedbackType, reason, dateFrom, dateTo]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const pageStats = useMemo(() => {
        let up = 0;
        let down = 0;
        const reasonCounts: Record<string, number> = {};
        for (const row of items) {
            const t = getFeedbackType(row);
            if (t === 'UP') up += 1;
            if (t === 'DOWN') down += 1;
            const r = pickString(row, 'reason');
            if (r) reasonCounts[r] = (reasonCounts[r] || 0) + 1;
        }
        let topReason = 'N/A';
        let topN = 0;
        for (const [k, n] of Object.entries(reasonCounts)) {
            if (n > topN) {
                topN = n;
                topReason = REASON_LABELS[k] || toTitle(k);
            }
        }
        return { up, down, topReason };
    }, [items]);

    if (initialLoading) {
        return <Preloader />;
    }

    return (
        <div className="space-y-6 min-h-0">
            {/* <div className="px-0">
                <h2 className="text-18-bold grey-900 mb-1 ps-2 md:ps-3">AI quality review</h2>
                <p className="text-14-reg grey-600 mb-0 ps-2 md:ps-3">Review chat feedback across the organization (Phase 1 queue).</p>
            </div> */}

            <Row>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <FeedbackStatCard title="Total feedback" value={String(total)} iconSrc={totalUsers} />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <FeedbackStatCard
                        title={'Positive'}
                        value={String(pageStats.up)}
                        iconSrc={chatBotInteraction}
                    // subtitle="This page"
                    />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <FeedbackStatCard
                        title={'Negative'}
                        value={String(pageStats.down)}
                        iconSrc={chatBotInteraction}
                    // subtitle="This page"
                    />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <FeedbackStatCard
                        title="Most common reason"
                        value={pageStats.topReason}
                        iconSrc={queriedSOP}
                        valueVariant="capsule"
                    // subtitle="This page"
                    />
                </Col>
            </Row>

            <div className="px-0">
                <div className="flex flex-col xl:flex-row gap-3 md:gap-4 items-stretch xl:items-end xl:justify-between pt-1 md:pt-2 flex-wrap">
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center ps-2 md:ps-3">
                        <div className="w-full sm:w-80 shrink-0">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                                    <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                                </span>
                                <input
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                    placeholder="Search"
                                    className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                />
                            </div>
                        </div>
                        <div className="relative" ref={typeDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setTypeDropdownOpen((v) => !v)}
                                className="w-full sm:w-48 h-10 px-3 py-2 flex items-center justify-between bg-white border border-gray-300 rounded-xl text-sm text-gray-700 transition-colors shadow-sm hover:bg-gray-50"
                            >
                                <span className={feedbackType ? 'text-gray-700' : 'text-gray-500'}>
                                    {!feedbackType ? 'Feedback type' : feedbackType === 'UP' ? 'Thumbs UP' : 'Thumbs DOWN'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {typeDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10">
                                    <div className="py-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFeedbackType(FEEDBACK_TYPE_FILTER_ALL);
                                                setTypeDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            All types
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFeedbackType('UP');
                                                setTypeDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Positive
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFeedbackType('DOWN');
                                                setTypeDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Negative
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="relative" ref={reasonDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setReasonDropdownOpen((v) => !v)}
                                className="w-full sm:w-56 h-10 px-3 py-2 flex items-center justify-between bg-white border border-gray-300 rounded-xl text-sm text-gray-700 transition-colors shadow-sm hover:bg-gray-50"
                            >
                                <span className={reason ? 'text-gray-700 truncate' : 'text-gray-500'}>
                                    {reason ? REASON_LABELS[reason] || reason : 'Reason'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${reasonDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {reasonDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
                                    <div className="py-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setReason(REASON_FILTER_ALL);
                                                setReasonDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            All reasons
                                        </button>
                                        {REASON_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => {
                                                    setReason(opt.value);
                                                    setReasonDropdownOpen(false);
                                                    setPage(1);
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row items-center gap-4">
                            {/* FROM GROUP */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-bold text-gray-500 m-0 leading-none">
                                    From
                                </label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => {
                                        setDateFrom(e.target.value);
                                        setPage(1);
                                    }}
                                    className="h-10 px-3 border border-gray-300 rounded-xl text-sm text-gray-700 bg-white"
                                />
                            </div>

                            {/* TO GROUP */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-bold text-gray-500 m-0 leading-none">
                                    To
                                </label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => {
                                        setDateTo(e.target.value);
                                        setPage(1);
                                    }}
                                    className="h-10 px-3 border border-gray-300 rounded-xl text-sm text-gray-700 bg-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Query</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Feedback</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Reason</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">User email</th>
                                {/* <th className="px-4 py-4 text-12-bold grey-700 uppercase">Source</th> */}
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Last updated</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-4 text-14-bold grey-800">
                                        Loading...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-6 text-center text-red-600">
                                        {error}
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center !text-gray-500">
                                        No feedback found
                                    </td>
                                </tr>
                            ) : (
                                items.map((row, idx) => {
                                    const q = pickString(row, 'user_query', 'userQuery', 'query', 'question', 'user_message', 'userMessage');
                                    const ft = getFeedbackType(row);
                                    const reasonRaw = pickString(row, 'reason');
                                    const reasonDisp = reasonRaw ? REASON_LABELS[reasonRaw] || toTitle(reasonRaw) : 'N/A';
                                    const user = pickString(row, 'userEmail', 'user_email', 'user', 'user_name', 'userName');
                                    const ds = pickFeedbackActivityIso(row);
                                    const rowKey = pickString(row, 'id', '_id') || `row-${idx}`;
                                    const dsNorm = ds ? normalizeUtcInputForApp(ds) : '';
                                    const dateCell =
                                        dsNorm && !Number.isNaN(new Date(dsNorm).getTime())
                                            ? formatToLocal(dsNorm, {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })
                                            : ds || 'N/A';

                                    return (
                                        <tr
                                            key={rowKey}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-4 py-4 text-14-bold grey-800 max-w-[280px]">
                                                <div className="truncate" title={q}>
                                                    {q || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-14-reg">
                                                {ft === 'UP' && (
                                                    <span className="inline-flex px-2 py-1 rounded-md text-12-bold bg-green-100 text-green-800">
                                                        UP
                                                    </span>
                                                )}
                                                {ft === 'DOWN' && (
                                                    <span className="inline-flex px-2 py-1 rounded-md text-12-bold bg-red-100 text-red-800">
                                                        DOWN
                                                    </span>
                                                )}
                                                {!ft && <span className="text-gray-500">N/A</span>}
                                            </td>
                                            <td className="px-4 py-4 text-14-reg grey-800">
                                                {reasonRaw ? (
                                                    <span className="px-2 py-1 rounded-md text-12-bold bg-gray-100 text-gray-800">
                                                        {reasonDisp}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-14-reg grey-800 whitespace-nowrap">{user || 'N/A'}</td>
                                            {/* <td className="px-4 py-4 text-14-reg grey-800">{source || '—'}</td> */}
                                            <td className="px-4 py-4 text-14-reg grey-800 whitespace-nowrap">{dateCell}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <button
                                                    className="p-2 rounded-lg hover:bg-gray-100"
                                                    onClick={async () => {
                                                        try {
                                                            setDetailItem(row);
                                                        } catch (e) {
                                                            console.error('Failed to load feedback', e);
                                                        }
                                                    }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="global-pagination px-4 md:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            type="button"
                            className="btn-next-prev"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <i className="iconoir-nav-arrow-left" />
                        </button>
                        <div className="flex items-center gap-1">
                            {getPageNumbers(page, totalPages).map((p: PageButton) => (
                                <button
                                    type="button"
                                    key={p.key}
                                    disabled={p.disabled}
                                    onClick={() => typeof p.page === 'number' && setPage(p.page)}
                                    className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="btn-next-prev"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <i className="iconoir-nav-arrow-right" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            max={100}
                            value={pageSize}
                            onChange={(e) => {
                                const n = parseInt(e.target.value, 10) || 10;
                                if (n >= 1 && n <= 100) {
                                    setPageSize(n);
                                    setPage(1);
                                }
                            }}
                            className="page-size-input"
                        />
                        <span className="text-14-bold grey-400">Rows per page</span>
                    </div>
                </div>
            </div>

            {detailItem && (
                <FeedbackDetailModal item={detailItem} onClose={() => setDetailItem(null)} formatToLocal={formatToLocal} />
            )}
        </div>
    );
};
